import React, { useState, useEffect } from "react";
import "./style/selectHost.scss";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../components/ui/Button/backButton";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import HostCombobox from "../../components/common/HostDropdown/HostCombobox";
import type { HostOption } from "../../components/common/HostDropdown/hostSelector";
import { touchlessService } from "../../services/api/touchlessService";
import { ROUTES } from "../../utils/constants";
import { useStrings } from "../../hooks/useStrings";

const SelectHost: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const { config, themeColors } = useAppConfig();
  const { updateForm, visitorTypeInfo } = useVisitorData();

  const hostField = visitorTypeInfo?.fields?.find(
    (f) => f.name === "Host" && f.status === "ACTIVE",
  );
  const isHostRequired = hostField?.setting === "MANDATORY";
  const isHostEditable = hostField?.isEditableForVisit !== false;

  // If the HOST field is not active in the current visitor type, skip this step.
  // Use replace:true so SelectHost is removed from history — prevents a redirect
  // loop when the user presses back from VisitorForm (which navigates to /select-host).
  useEffect(() => {
    if (visitorTypeInfo && !hostField) {
      navigate(`/${ROUTES.VISITOR_FORM}`, { replace: true });
    }
  }, [visitorTypeInfo, hostField, navigate]);

  const [selectedHostId, setSelectedHostId] = useState("");
  const [selectedHostName, setSelectedHostName] = useState("");
  const loadHostOptions = async (searchTerm: string): Promise<HostOption[]> => {
    if (!config) return [];
    if (searchTerm.length < 3) return [];
    try {
      const hosts = await touchlessService.getUsersDetailsByName(
        searchTerm,
        config,
      );
      return (hosts || []).map((host) => ({
        value: host.id,
        label: `${host.firstName} ${host.lastName}`.trim(),
        firstName: host.firstName,
        lastName: host.lastName,
        title: host.title,
        department: host.department,
      }));
    } catch {
      return [];
    }
  };

  const handleHostSelect = (
    hostId: string,
    firstName: string,
    lastName: string,
  ) => {
    setSelectedHostId(hostId);
    setSelectedHostName(`${firstName} ${lastName}`.trim());
  };

  const handleClearHost = () => {
    setSelectedHostId("");
    setSelectedHostName("");
  };

  const handleContinue = () => {
    updateForm({ hostId: selectedHostId, hostName: selectedHostName });
    navigate(`/${ROUTES.VISITOR_FORM}`);
  };

  const onBack = () => {
    navigate("/full-name");
  };

  return (
    <div className="main-screen">
      <div className="screen-outer">
        <div
          className="screen-header-fixed"
          data-testid="select-host-header-fixed"
        >
          <div
            className="header-fixed-left"
            data-testid="select-host-header-left"
          >
            <BackButton onBack={onBack} />
          </div>
          <div
            className="header-fixed-right"
            data-testid="select-host-header-right"
          ></div>
        </div>
        <div className="screen-inner">
          <div className="screen-subinner">
            <div className="screen-header-logo"></div>
            <div className="screen-body">
              <h5 className="screen-title" data-testid="select-host-title">
                {S.SELECT_HOST.TITLE}
              </h5>
              <p className="screen-subtitle" data-testid="select-host-subtitle">
                {S.SELECT_HOST.SUBTITLE}
              </p>
              <div className="mt-3" data-testid="select-host-button-wrapper">
                <HostCombobox
                  selectedHostId={selectedHostId}
                  selectedHostName={selectedHostName}
                  onHostSelect={handleHostSelect}
                  onClearHost={handleClearHost}
                  loadHostOptions={loadHostOptions}
                  btnColor={themeColors.btnColor}
                  fieldId="select-host-dropdown"
                  dataTestId="select-host-dropdown"
                  disabled={!isHostEditable}
                />
              </div>
              <div
                className="select-host-footer"
                data-testid="select-host-continue-wrapper"
              >
                <button
                  type="button"
                  className="btn btn-primary btn-shadow btn-width-full"
                  disabled={isHostRequired && !selectedHostId}
                  onClick={handleContinue}
                  data-testid="select-host-continue-button"
                >
                  {S.COMMON.CONTINUE} &nbsp;&nbsp;
                  <i className="bi bi-arrow-right-short"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectHost;
